import React, { useState, useEffect } from 'react';
import { assignmentAPI } from '../services/api';
import AdminNavbar from '../components/AdminNavbar';

const StudentQuiz = () => {
  const [studentId, setStudentId] = useState('');
  const [assignment, setAssignment] = useState(null);
  const [currentPart, setCurrentPart] = useState(1);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [completed, setCompleted] = useState(false);
  const [score, setScore] = useState(0);
  const [detailedResults, setDetailedResults] = useState([]);
  const [visitedQuestions, setVisitedQuestions] = useState(new Set());
  const [timeRemaining, setTimeRemaining] = useState(60 * 60); // 60 minutes in seconds
  const [timerInterval, setTimerInterval] = useState(null);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [unansweredCount, setUnansweredCount] = useState(0);

  // Get current questions without depending on currentQuestion
  const getCurrentQuestions = () => {
    if (!assignment) return [];
    
    switch (currentPart) {
      case 1:
        return assignment.questions.part1;
      case 2:
        return assignment.questions.part2;
      case 3:
        return assignment.questions.part3;
      default:
        return [];
    }
  };

  // Mark current question as visited when it changes
  useEffect(() => {
    const questions = getCurrentQuestions();
    const currentQuestion = questions[currentIndex];
    
    if (currentQuestion) {
      setVisitedQuestions(prev => {
        const newSet = new Set(prev);
        newSet.add(currentQuestion._id.toString());
        return newSet;
      });
    }
  }, [currentPart, currentIndex, assignment]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [timerInterval]);

  const fetchAssignment = async () => {
    try {
      setLoading(true);
      setMessage('');
      
      const response = await assignmentAPI.getByStudent(studentId);
      
      if (response.data.length === 0) {
        setMessage('No active assignment found for this student');
        setAssignment(null);
      } else {
        // Find the first incomplete assignment
        const activeAssignment = response.data.find(a => !a.completed);
        
        if (activeAssignment) {
          // Fetch the full assignment with questions
          const fullAssignment = await assignmentAPI.getById(activeAssignment._id);
          setAssignment(fullAssignment.data);
          
          // Initialize answers object
          const initialAnswers = {};
          ['part1', 'part2', 'part3'].forEach(part => {
            fullAssignment.data.questions[part].forEach(q => {
              initialAnswers[q._id] = null;
            });
          });
          setAnswers(initialAnswers);
          
          // Start the timer
          startTimer();
        } else {
          setMessage('No active assignments found. All assignments are completed.');
        }
      }
    } catch (error) {
      console.error('Error fetching assignment:', error);
      setMessage('Error fetching assignment. Please check the student ID.');
    } finally {
      setLoading(false);
    }
  };

  const startTimer = () => {
    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          handleSubmitQuiz(); // Auto-submit when time expires
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    setTimerInterval(interval);
  };

  // Format time for display
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleStudentIdSubmit = (e) => {
    e.preventDefault();
    if (studentId) {
      fetchAssignment();
    }
  };

  const handleAnswerSelect = (questionId, optionIndex) => {
    setAnswers({
      ...answers,
      [questionId]: optionIndex
    });
  };

  const handleNext = () => {
    const questions = getCurrentQuestions();
    
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else if (currentPart < 3) {
      setCurrentPart(currentPart + 1);
      setCurrentIndex(0);
    } else {
      // Check for unanswered questions instead of immediately submitting
      checkUnansweredAndConfirm();
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else if (currentPart > 1) {
      setCurrentPart(currentPart - 1);
      const prevQuestions = currentPart === 2 
        ? assignment.questions.part1 
        : assignment.questions.part2;
      setCurrentIndex(prevQuestions.length - 1);
    }
  };

  const jumpToQuestion = (part, index) => {
    setCurrentPart(part);
    setCurrentIndex(index);
  };

  const checkUnansweredAndConfirm = () => {
    const totalQuestions = 
      assignment.questions.part1.length + 
      assignment.questions.part2.length + 
      assignment.questions.part3.length;
      
    const answeredCount = Object.values(answers).filter(a => a !== null).length;
    const unanswered = totalQuestions - answeredCount;
    
    setUnansweredCount(unanswered);
    setShowSubmitConfirm(true);
  };

  const handleSubmitQuiz = async () => {
    try {
      setLoading(true);
      
      // Clear the timer
      if (timerInterval) {
        clearInterval(timerInterval);
      }
      
      // Calculate score
      let correct = 0;
      let total = 0;
      const results = [];
      
      // Process each part
      ['part1', 'part2', 'part3'].forEach(part => {
        assignment.questions[part].forEach(question => {
          total++;
          const isCorrect = answers[question._id] === question.correctAnswer;
          if (isCorrect) {
            correct++;
          }
          
          // Store detailed result for this question
          results.push({
            questionId: question._id,
            questionText: question.text,
            userAnswer: answers[question._id],
            correctAnswer: question.correctAnswer,
            options: question.options,
            isCorrect: isCorrect,
            part: part
          });
        });
      });
      
      const scorePercentage = Math.round((correct / total) * 100);
      
      // Submit to API
      await assignmentAPI.complete(assignment._id, { 
        score: scorePercentage,
      });
      
      setScore(scorePercentage);
      setDetailedResults(results);
      setCompleted(true);
      setShowSubmitConfirm(false);
    } catch (error) {
      console.error('Error submitting quiz:', error);
      setMessage('Error submitting quiz. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderSubmitConfirmation = () => {
    if (!showSubmitConfirm) return null;
    
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1050
      }}>
        <div className="card" style={{width: '400px'}}>
          <div className="card-header">
            <h5 className="mb-0">Confirm Submission</h5>
          </div>
          <div className="card-body">
            {unansweredCount > 0 ? (
              <p className="text-warning">You have {unansweredCount} unanswered questions. Are you sure you want to submit?</p>
            ) : (
              <p>Are you ready to submit your quiz?</p>
            )}
          </div>
          <div className="card-footer">
            <button className="btn btn-secondary me-2" onClick={() => setShowSubmitConfirm(false)}>
              Return to Quiz
            </button>
            <button className="btn btn-primary" onClick={handleSubmitQuiz}>
              Submit Quiz
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // Pre-calculate values for rendering the quiz
  const questions = getCurrentQuestions();
  const currentQuestion = assignment && !completed && questions[currentIndex];

  // Render the quiz interface
  return (
    <div className="container-fluid px-4 mt-4" style={{ maxWidth: "1400px", margin: "0 auto" }}>
      <h1>Student Quiz</h1>
      
      {!assignment && !completed && (
        <div className="card">
          <div className="card-body">
            <h5 className="card-title">Enter Your Student ID</h5>
            
            {message && (
              <div className="alert alert-info" role="alert">
                {message}
              </div>
            )}
            
            <form onSubmit={handleStudentIdSubmit}>
              <div className="mb-3">
                <label htmlFor="studentId" className="form-label">Student ID</label>
                <input
                  type="text"
                  className="form-control"
                  id="studentId"
                  value={studentId}
                  onChange={(e) => setStudentId(e.target.value)}
                  required
                />
              </div>
              
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Loading...
                  </>
                ) : 'Start Quiz'}
              </button>
            </form>
          </div>
        </div>
      )}
      
      {/* Quiz content - no IIFE here */}
      {assignment && !completed && currentQuestion && (
        <div className="card">
          <div className="card-header">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                Part {currentPart} - Question {currentIndex + 1} of {questions.length}
              </h5>
              <div className="text-danger fw-bold">
                Time Remaining: {formatTime(timeRemaining)}
              </div>
            </div>
          </div>
          
          <div className="card-body">
            <div className="row">
              {/* Question content - Left column (8 columns wide) */}
              <div className="col-md-8">
                <div>
                  {/* Larger question text */}
                  <h4 className="card-title mb-4" style={{ fontSize: '1.5rem', fontWeight: '500' }}>
                    {currentQuestion.text}
                  </h4>
                  
                  {/* Options with increased size and spacing */}
                  <div className="mb-4">
                    {currentQuestion.options.map((option, index) => (
                      <div className="form-check mb-3" key={index} style={{ fontSize: '1.2rem' }}>
                        <input
                          className="form-check-input"
                          type="radio"
                          name={`question-${currentQuestion._id}`}
                          id={`option-${index}`}
                          checked={answers[currentQuestion._id] === index}
                          onChange={() => handleAnswerSelect(currentQuestion._id, index)}
                          style={{ transform: 'scale(1.2)', marginTop: '0.3rem' }}
                        />
                        <label 
                          className="form-check-label" 
                          htmlFor={`option-${index}`}
                          style={{ paddingLeft: '0.5rem' }}
                        >
                          {option}
                        </label>
                      </div>
                    ))}
                  </div>
                  
                  <div className="d-flex justify-content-between">
                    <button 
                      className="btn btn-secondary" 
                      onClick={handlePrevious}
                      disabled={currentPart === 1 && currentIndex === 0}
                    >
                      Previous
                    </button>
                    
                    <button 
                      className="btn btn-primary" 
                      onClick={handleNext}
                    >
                      {currentPart === 3 && currentIndex === questions.length - 1 ? 
                        'Submit Quiz' : 'Next'}
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Question Navigator - Right column (4 columns wide) */}
              <div className="col-md-4">
                <div className="card">
                  <div className="card-header">
                    <h6 className="mb-0">Question Navigator</h6>
                  </div>
                  <div className="card-body">
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      <span className="badge bg-secondary">Not Visited</span>
                      <span className="badge bg-info">Current</span>
                      <span className="badge bg-warning">Visited</span>
                      <span className="badge bg-success">Answered</span>
                    </div>
                    
                    {[1, 2, 3].map(part => (
                      <div key={part} className="mb-3">
                        <h6>Part {part}</h6>
                        <div className="d-flex flex-wrap gap-1">
                          {assignment.questions[`part${part}`].map((q, idx) => {
                            // Determine button status
                            let bgClass = "secondary"; // Not visited
                            if (answers[q._id] !== null) bgClass = "success"; // Answered
                            else if (visitedQuestions.has(q._id.toString())) bgClass = "warning"; // Visited
                            
                            // Current question
                            if (currentPart === part && currentIndex === idx) bgClass = "info";
                            
                            return (
                              <button 
                                key={q._id}
                                className={`btn btn-sm btn-${bgClass}`}
                                onClick={() => jumpToQuestion(part, idx)}
                                style={{ 
                                  width: '36px', 
                                  height: '36px', 
                                  padding: '0',
                                  margin: '2px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                {idx + 1}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {completed && (
        <div className="card">
          <div className="card-body">
            <h3 className="text-center">Quiz Completed!</h3>
            
            <div className="text-center mb-4">
              <h4>Your Score: {score}%</h4>
              <p>You answered {detailedResults.filter(r => r.isCorrect).length} out of {detailedResults.length} questions correctly.</p>
            </div>
            
            {/* Result Summary by Part */}
            <div className="row mb-4">
              <div className="col-md-4">
                <div className="card">
                  <div className="card-body text-center">
                    <h5>Part 1</h5>
                    <p className="mb-0">
                      {detailedResults.filter(r => r.part === 'part1' && r.isCorrect).length} / {detailedResults.filter(r => r.part === 'part1').length}
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card">
                  <div className="card-body text-center">
                    <h5>Part 2</h5>
                    <p className="mb-0">
                      {detailedResults.filter(r => r.part === 'part2' && r.isCorrect).length} / {detailedResults.filter(r => r.part === 'part2').length}
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card">
                  <div className="card-body text-center">
                    <h5>Part 3</h5>
                    <p className="mb-0">
                      {detailedResults.filter(r => r.part === 'part3' && r.isCorrect).length} / {detailedResults.filter(r => r.part === 'part3').length}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Option to view detailed results */}
            <div className="mb-3">
              <button 
                className="btn btn-outline-primary" 
                type="button" 
                data-bs-toggle="collapse" 
                data-bs-target="#detailedResults" 
                aria-expanded="false" 
                aria-controls="detailedResults"
              >
                View Detailed Results
              </button>
            </div>
            
            <div className="collapse" id="detailedResults">
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Question</th>
                      <th>Your Answer</th>
                      <th>Correct Answer</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailedResults.map((result, idx) => (
                      <tr key={result.questionId}>
                        <td>{idx + 1}</td>
                        <td>{result.questionText}</td>
                        <td>{result.userAnswer !== null ? result.options[result.userAnswer] : 'Not answered'}</td>
                        <td>{result.options[result.correctAnswer]}</td>
                        <td>
                          {result.isCorrect ? 
                            <span className="badge bg-success">Correct</span> : 
                            <span className="badge bg-danger">Incorrect</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className="text-center mt-3">
              <button 
                className="btn btn-primary" 
                onClick={() => {
                  setAssignment(null);
                  setCompleted(false);
                  setStudentId('');
                  setCurrentPart(1);
                  setCurrentIndex(0);
                  setAnswers({});
                  setDetailedResults([]);
                  setVisitedQuestions(new Set());
                  setTimeRemaining(60 * 60);
                }}
              >
                Start New Quiz
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Submit Confirmation Modal */}
      {renderSubmitConfirmation()}
    </div>
  );
};

export default StudentQuiz;